import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight, BookOpen, CheckSquare, Clock, FileText, HelpCircle, Landmark, Loader2, PoundSterling, Search, UserCog,
  Users,
} from 'lucide-react';
import { api } from '@/services/api';
import { useAuthStatus, useLogout } from '@/hooks/useApi';
import { useModuleContext } from '@/contexts/ModuleContext';
import { StatusBadge } from '@/components/kit';
import { formatDate, formatMoney } from '@/utils/formatting';
import { buildCommands, matchCommands, type Command } from './commands';

/**
 * ⌘K / Ctrl+K: one box to find anything (UX Stage C2). Search results come from GET /api/search, which cuts every
 * group by module, company and role on the server; commands ("Go to Worklist", "Import bank statements"…) come
 * from the user's own menu. Empty box: recent picks and the commands. Keyboard: arrows (and Home/End) move,
 * Enter opens, Esc closes and gives focus back. The ARIA combobox pattern: focus stays in the input and the
 * highlighted option is aria-activedescendant.
 */

export interface SearchItem {
  id: string; title: string; subtitle?: string | null; status?: string | null; href: string;
  amount?: string | null; currency?: string | null; date?: string | null;
}
interface SearchGroup { key: string; label: string; items: SearchItem[] }
interface SearchResponse { q: string; groups: SearchGroup[]; took_ms: number }

const GROUP_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  reconciliations: CheckSquare, bank_accounts: Landmark, journals: BookOpen, customers: Users, invoices: FileText,
  amounts: PoundSterling, users: UserCog, help: HelpCircle, recent: Clock,
};

const RECENT_MAX = 6;
const recentKey = (user: string) => `mp.cmdk.recent.${user}`;
type Recent = SearchItem & { group: string };

function readRecent(user: string): Recent[] {
  try { return JSON.parse(window.localStorage.getItem(recentKey(user)) || '[]').slice(0, RECENT_MAX); } catch { return []; }
}
function remember(user: string, item: Recent) {
  try {
    const next = [item, ...readRecent(user).filter(r => r.id !== item.id)].slice(0, RECENT_MAX);
    window.localStorage.setItem(recentKey(user), JSON.stringify(next));
  } catch { /* not kept */ }
}

function useDebounced<T>(value: T, ms = 150): T {
  const [v, setV] = React.useState(value);
  React.useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

type Row =
  | { kind: 'item'; key: string; group: string; groupLabel: string; item: SearchItem }
  | { kind: 'command'; key: string; group: string; groupLabel: string; command: Command };

const PaletteContext = React.createContext<{ open: () => void } | null>(null);

/** Open the palette from anywhere below the provider (the sidebar's search box, the phone top bar). */
export const useCommandPalette = () => React.useContext(PaletteContext) ?? { open: () => {} };

export const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
export const shortcutLabel = () => (isMac() ? '⌘K' : 'Ctrl K');

export const CommandPaletteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const ctx = React.useMemo(() => ({ open: () => setOpen(true) }), []);
  return (
    <PaletteContext.Provider value={ctx}>
      {children}
      {open && <CommandPalette onClose={() => setOpen(false)} />}
    </PaletteContext.Provider>
  );
};

export const CommandPalette: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const navigate = useNavigate();
  const logout = useLogout();
  const { data: auth } = useAuthStatus();
  const { hasModule } = useModuleContext();
  const user = auth?.user;
  const userKey = user ? String((user as { id?: number }).id ?? user.username) : 'anon';
  const roles = user?.roles ?? [];
  const [q, setQ] = React.useState('');
  const [active, setActive] = React.useState(0);
  const input = React.useRef<HTMLInputElement>(null);
  const list = React.useRef<HTMLDivElement>(null);
  const listId = React.useId();
  const debounced = useDebounced(q.trim());

  React.useEffect(() => {
    const back = document.activeElement as HTMLElement | null;
    input.current?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = overflow; back?.focus?.(); };
  }, []);

  const searching = debounced.length >= 2;
  const results = useQuery({
    queryKey: ['global-search', debounced],
    queryFn: () => api.get<SearchResponse>('/api/search', { params: { q: debounced } }).then(r => r.data),
    enabled: searching,
    staleTime: 30_000,
  });

  const commands = React.useMemo(() => buildCommands({ hasModule, roles }), [hasModule, roles.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows: Row[] = React.useMemo(() => {
    const out: Row[] = [];
    const text = q.trim();
    if (!text) {
      for (const r of readRecent(userKey)) out.push({ kind: 'item', key: `recent:${r.id}`, group: 'recent', groupLabel: 'Recent', item: r });
    }
    if (searching && results.data && results.data.q === debounced) {
      for (const g of results.data.groups) {
        for (const item of g.items) out.push({ kind: 'item', key: `${g.key}:${item.id}`, group: g.key, groupLabel: g.label, item });
      }
    }
    for (const c of matchCommands(commands, text).slice(0, text ? 6 : 8)) {
      out.push({ kind: 'command', key: c.id, group: 'commands', groupLabel: text ? 'Commands' : 'Go to', command: c });
    }
    return out;
  }, [q, debounced, searching, results.data, commands, userKey]);

  React.useEffect(() => { setActive(0); }, [debounced, q === '']);   // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    list.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const choose = (row: Row | undefined) => {
    if (!row) return;
    onClose();
    if (row.kind === 'command') {
      const c = row.command;
      if (c.run === 'logout') { logout.mutate(); return; }
      if (c.external && c.href) { window.open(c.href, '_blank', 'noopener,noreferrer'); return; }
      if (c.href) navigate(c.href);
      return;
    }
    remember(userKey, { ...row.item, group: row.group === 'recent' ? (row.item as Recent).group : row.group });
    navigate(row.item.href);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => (rows.length ? (a + 1) % rows.length : 0)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => (rows.length ? (a - 1 + rows.length) % rows.length : 0)); }
    else if (e.key === 'Home' && rows.length) { e.preventDefault(); setActive(0); }
    else if (e.key === 'End' && rows.length) { e.preventDefault(); setActive(rows.length - 1); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(rows[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
    else if (e.key === 'Tab') { e.preventDefault(); }                 // focus stays in the box (it is a modal)
  };

  const optionId = (i: number) => `${listId}-opt-${i}`;
  const loading = searching && (results.isFetching || debounced !== q.trim());
  const noMatch = searching && !loading && rows.length === 0;

  // group headings in order of first appearance
  let lastGroup = '';
  return createPortal(
    <div className="fixed inset-0 z-[210] flex items-start justify-center px-mp-4 pt-[12vh]" onKeyDown={onKeyDown}>
      <div className="absolute inset-0 bg-mp-text opacity-40" aria-hidden onClick={onClose} data-testid="palette-backdrop" />
      <div role="dialog" aria-modal="true" aria-label="Search MatchPoint"
           className="relative flex max-h-[70vh] w-full max-w-2xl flex-col overflow-hidden rounded-mp-md border border-mp-border bg-mp-surface font-mp-sans text-mp-base text-mp-text shadow-xl">
        <div className="flex items-center gap-mp-2 border-b border-mp-border px-mp-4">
          {loading ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-mp-text-muted" aria-hidden />
                   : <Search className="h-4 w-4 shrink-0 text-mp-text-muted" aria-hidden />}
          <input ref={input} value={q} onChange={e => setQ(e.target.value)} type="text" autoComplete="off" spellCheck={false}
                 placeholder="Search accounts, customers, invoices, amounts, journals… or type a command"
                 role="combobox" aria-expanded="true" aria-controls={listId} aria-autocomplete="list"
                 aria-activedescendant={rows[active] ? optionId(active) : undefined} aria-label="Search"
                 className="h-12 min-w-0 flex-1 bg-transparent text-mp-base text-mp-text placeholder:text-mp-text-muted focus:outline-none" />
          <kbd className="hidden rounded-mp-sm border border-mp-border px-mp-1 text-mp-sm text-mp-text-muted sm:inline">Esc</kbd>
        </div>
        <div ref={list} id={listId} role="listbox" aria-label="Results" className="overflow-y-auto py-mp-1">
          {results.isError && (
            <p className="px-mp-4 py-mp-3 text-mp-sm text-mp-danger" role="alert">Search isn't available right now. Commands still work.</p>
          )}
          {noMatch && (
            <p className="px-mp-4 py-mp-6 text-center text-mp-text-muted" data-testid="palette-empty">
              Nothing found for “{q.trim()}”.
            </p>
          )}
          {rows.map((row, i) => {
            const heading = row.group !== lastGroup ? row.groupLabel : null;
            lastGroup = row.group;
            const Icon = row.kind === 'command' ? row.command.icon : (GROUP_ICON[row.group] ?? ArrowRight);
            const selected = i === active;
            return (
              <React.Fragment key={row.key}>
                {heading && (
                  <div className="px-mp-4 pb-mp-1 pt-mp-3 text-mp-sm font-medium uppercase tracking-wide text-mp-text-muted" aria-hidden>
                    {heading}
                  </div>
                )}
                <div id={optionId(i)} role="option" aria-selected={selected} data-index={i}
                     aria-label={row.kind === 'command' ? row.command.label : `${row.groupLabel}: ${row.item.title}`}
                     onMouseMove={() => { if (!selected) setActive(i); }} onClick={() => choose(row)}
                     className={`mx-mp-2 flex cursor-pointer items-center gap-mp-3 rounded-mp-sm px-mp-2 py-mp-2 ${selected ? 'bg-mp-surface-2' : ''}`}>
                  <Icon className="h-4 w-4 shrink-0 text-mp-text-muted" aria-hidden />
                  {row.kind === 'command' ? (
                    <span className="min-w-0 flex-1 truncate">{row.command.label}</span>
                  ) : (
                    <>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{row.item.title}</span>
                        {(row.item.subtitle || row.item.date) && (
                          <span className="block truncate text-mp-sm text-mp-text-muted">
                            {[row.item.subtitle, row.item.date && formatDate(row.item.date)].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </span>
                      {row.item.amount != null && (
                        <span className="shrink-0 tabular-nums">{formatMoney(row.item.amount, row.item.currency ?? null)}</span>
                      )}
                      {row.item.status && <StatusBadge status={row.item.status} />}
                    </>
                  )}
                  {selected && <ArrowRight className="h-4 w-4 shrink-0 text-mp-text-muted" aria-hidden />}
                </div>
              </React.Fragment>
            );
          })}
        </div>
        <div className="hidden items-center justify-between gap-mp-3 border-t border-mp-border px-mp-4 py-mp-2 text-mp-sm text-mp-text-muted sm:flex">
          <span>↑↓ to move · Enter to open · Esc to close</span>
          <span>{shortcutLabel()} opens this from anywhere</span>
        </div>
      </div>
    </div>,
    document.body,
  );
};
